/**
 * [2023-09-08.1119 by Brian]
 *
 *                  --- AssociateReferenceCodes ---
 *
 *   A standardized way of associating billing codes or other reference codes
 *   with _something_. Where _something_ is a test, a supplement, etc.
 *
 *   All this does is manage the UI component and what is contained in the
 *   data structures. Support for the REST calls is left as an exercise for
 *   the reader ;-)
 *
 * Setup:
 *
 *   The CUP-specific hook useForm() must be used.
 *
 *   Designed to be _inside_ of a <form> element.
 *
 *   The 'value' and 'patchValue' from useForm() must be passed in.
 *
 *   The the key on 'value' _must_ be 'referenceCodes'.
 *
 * Usage:
 *
 *   <AssociateReferenceCodes
 *      value={value}
 *      patchValue={patchValue}
 *      className="col-span-4 pt-8"
 *      codeSet={[CodeSets.CPT, CodeSets.SERVICE]}
 *      endpoint="/codes"
 *      noCodesMessage="No codes are associated with this test."
 *   />
 *
 * Security:
 *
 *   This component will always show up. Originally, the idea was for it
 *   to detect when it should or should-not appear, but that was
 *   problematic for a component shared across multiple apps. So, if it
 *   should only appear conditionally, make sure you keep this out of
 *   the DOM with:
 *
 *     { hasEHR && (<AssociateReferenceCodes ... />) }
 *
 *   ...or something similar. Gate on the clinic's EHR entitlement (hasEHR /
 *   Clinic.ehr), NOT on hasAccess(FeatureFlags.ehr): that flag resolves to
 *   Clinic.ehr AND an 'ehr' row in the clinic's flag group, so it hides the
 *   component from real EHR clinics whose group lacks that row.
 *
 *   You may also pass it a 'hasAccess' prop to control its visibility.
 *
 * Technical Debt:
 *
 *   Maybe a decent idea to not hard code 'referenceCodes' as the key.
 *
 *   The caller could provide a component-specific patchValue instead
 *   of the one from useForm() being passed around. This would fix the
 *   hard-coded 'referenceCodes' issue.
 *
 *   The caller could provide a clone method too. But at that point,
 *   this does not simplify matters much. Hmmm...
 *
 * Parameters:
 *   Details of each parameter follow. Optional ones have sane defaults
 *   (hopefully).
 *
 *   value:
 *      The 'value' from useForm() hook.
 *
 *   patchValue:
 *      The 'patchValue' function from useForm().
 *
 *   className:
 *     Optional. A CSS class name to apply to the outermost <div>.
 *
 *   addClassName:
 *     Optional. A CSS class name to apply to the outermost <div>
 *     _in addition_ to the default ones or the ones passed in
 *     className.
 *
 *   endpoint:
 *     Optional. The endpoint used for the REST call. It defaults
 *     to the CUP settings endpoint, but to be used with CUP Admin
 *     it needs to be overridden.
 *
 *  label:
 *     Optional. Whatever you want the introductory heading to be.
 *
 *  noneText:
 *     Optional. This is the initial text that appears _before_ the
 *     user starts typing into the autocomplete field.
 *
 *  codeSet:
 *     Optional. This is a single or an array of CodeSets.
 *
 *  noCodesMessage:
 *     Optional. This message appears when the length of
 *     'value.referenceCodes' is zero.
 *
 *  compact:
 *    Optional. Makes the font smaller and gets rid of some
 *    extra spacing.
 *
 *  topGutter:
 *    Optional. A React node to be placed above the component.
 *    Depending on what type of layout this component is going
 *    into, this might be useful. If you're in a grid, 'mt-x'
 *    seem to be ignored, for instance.
 *
 *  labelTooltip:
 *    Optional. A tooltip to be displayed next to the label.
 *
 *  hasAccess:
 *    Optional. Boolean. Can the user use this component?
 *
 *  local:
 *    Optional. Boolean. When true, only the local or clinic-
 *    specific codes are searched. Otherwise, the local AND
 *    the global list is searched.
 */
import { cn } from '@/lib/utils';
import { CodeSets } from '@chiroup/core/types/BillingCode.type';
import { ReferenceCodeDisplay } from '@chiroup/core/types/ReferenceCode.type';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React, { use, useId, useMemo, useState } from 'react';
import { ToastContext, ToastTypes } from '../../../../apps/chiroup/src/contexts/toast.context';
import { CodeAutocomplete } from './CodeAutocomplete';
import { TrivialTooltip } from './TrivialTooltip';

type Props = {
  value: Partial<any>;
  patchValue?: (val: Partial<any>) => void;
  setVisitForm?: React.Dispatch<React.SetStateAction<any>>;
  className?: string;
  addClassName?: string;
  endpoint?: string;
  label?: string;
  noneText?: string;
  codeSet?: CodeSets | CodeSets[];
  noCodesMessage?: string;
  compact?: boolean;
  topGutter?: React.ReactNode;
  labelTooltip?: string;
  associatedCodes?: ReferenceCodeDisplay[];
  complaintIndex?: number;
  conditionIndex?: number;
  clinicId?: number;
  nonConditionReferenceCodes?: boolean;
  rightOfLabel?: React.ReactNode;
  hideNoCodesMessage?: boolean;
  local?: boolean;
  hasAccess?: boolean;
  associatedCodesLabel?: string;
  disabled?: boolean;
  // Enables drag-reorder + 1..n numbering of the associated codes.
  sortable?: boolean;
};

const SortableCodeRow: React.FC<{
  o: ReferenceCodeDisplay;
  position: number;
  textSize: string;
  disabled?: boolean;
  onDelete: () => void;
}> = ({ o, position, textSize, disabled, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: o.code,
    disabled,
    transition: {
      duration: 150,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'flex flex-row gap-2 items-center',
        textSize,
        disabled ? '' : isDragging ? 'cursor-grabbing' : 'cursor-grab'
      )}
    >
      <div className="grow flex flex-row gap-2 items-center p-2 border-2 rounded-md bg-gray-50 dark:bg-dark-gray-700 border-grey-300 dark:border-dark-gray-600 text-gray-900 dark:text-dark-gray-100">
        <span className="shrink-0 w-5 text-right tabular-nums text-gray-400 dark:text-dark-gray-500">
          {position}.
        </span>
        <span>{`${o.code || ''} - ${o.description || ''}`}</span>
      </div>
      <div
        className="hover:text-primary-500 pr-1 active:text-primary-600 flex justify-center shrink pt-2 text-gray-500 dark:text-dark-gray-400 cursor-pointer items-top"
        title="Remove this code from the list."
        onMouseDown={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
        onClick={() => {
          if (disabled) return;
          onDelete();
        }}
      >
        <Icon icon={Icons.trash} className="w-5 h-5" />
      </div>
    </div>
  );
};

export const AssociateReferenceCodes: React.FC<Props> = ({
  value,
  patchValue,
  setVisitForm,
  className,
  addClassName,
  endpoint,
  label,
  noneText = 'Type to search for available codes...',
  codeSet = CodeSets.ICD10CM,
  noCodesMessage = 'No codes are associated with this item.',
  compact = false,
  topGutter,
  labelTooltip,
  associatedCodes,
  complaintIndex,
  conditionIndex,
  clinicId,
  nonConditionReferenceCodes = false,
  rightOfLabel,
  hideNoCodesMessage,
  local = false,
  hasAccess = true,
  associatedCodesLabel = 'Associated Billing Codes:',
  disabled = false,
  sortable = false,
}) => {
  const { createToast } = use(ToastContext);
  const [search, setSearch] = useState('');
  const dndId = useId();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 10, delay: 150, tolerance: 5 } })
  );
  const measuring = {
    droppable: {
      strategy: MeasuringStrategy.BeforeDragging,
    },
  };
  label =
    label ||
    (Array.isArray(codeSet)
      ? `Associated ${local ? 'Clinic ' : ''}Billing Codes`
      : `Associated ${local ? 'Clinic ' : ''}Diagnostic Codes`);
  const [onDeck, setOnDeck] = useState<{
    codeSet: CodeSets | undefined;
    code: string;
    desc: string;
    repeat: boolean;
  }>({
    codeSet: undefined,
    code: '',
    desc: '',
    repeat: false,
  });

  const filteredCodes =
    associatedCodes?.filter(
      ac => !value?.referenceCodes?.some((v: ReferenceCodeDisplay) => v.code === ac.code)
    ) || [];

  const klasses = (
    className
      ? className.split(' ')
      : [
          'bg-gray-100 dark:bg-dark-gray-800',
          'rounded-md',
          'border border-gray-300 dark:border-dark-gray-600',
          'h-full',
          'flex flex-col justify-between',
        ]
  ).concat((addClassName || '').split(' '));

  const updateNonConditionReferenceCodes = (prev: any, complaintIdx: number, deck: any) => {
    return {
      ...prev,
      soapCustomEditWarning: {
        ...prev.soapCustomEditWarning,
        assessment: false,
      },
      encounterInfo: {
        ...prev.encounterInfo,
        complaints: prev.encounterInfo.complaints?.map((complaint: any, i: number) => {
          if (i === complaintIdx) {
            const assessment = complaint.assessment || {};
            const nonConditions = assessment.nonConditions || {};
            const referenceCodes = nonConditions.referenceCodes || [];

            return {
              ...complaint,
              assessment: {
                ...assessment,
                nonConditions: {
                  ...nonConditions,
                  referenceCodes: [
                    ...referenceCodes,
                    {
                      codeSet: deck.codeSet,
                      code: deck.code,
                      description: deck.desc,
                    },
                  ],
                },
              },
            };
          }

          return complaint;
        }),
      },
    };
  };

  const updateReferenceCodes = (
    prev: any,
    complaintIdx: number,
    conditionIdx: number,
    deck: any
  ) => {
    return {
      ...prev,
      soapCustomEditWarning: {
        ...prev.soapCustomEditWarning,
        assessment: false,
      },
      encounterInfo: {
        ...prev.encounterInfo,
        complaints: prev.encounterInfo.complaints.map((complaint: any, i: number) => {
          if (i === complaintIdx) {
            return {
              ...complaint,
              assessment: {
                ...complaint.assessment,
                conditions: complaint.assessment.conditions.map((condition: any, j: number) => {
                  if (j === conditionIdx) {
                    return {
                      ...condition,
                      referenceCodes: [
                        ...(condition.referenceCodes || []),
                        {
                          codeSet: deck.codeSet,
                          code: deck.code,
                          description: deck.desc,
                        },
                      ],
                    };
                  }

                  return condition;
                }),
              },
            };
          }

          return complaint;
        }),
      },
    };
  };

  const addCodeFromValues = (
    codeSetVal: CodeSets | undefined,
    codeVal: string,
    descVal: string
  ) => {
    if (!codeVal || disabled) return;

    const existingCodes = (value?.referenceCodes || []).reduce(
      (acc: any, curr: any) => ({ ...acc, [curr.code]: curr }),
      {}
    );

    if (existingCodes[codeVal]) {
      if (createToast) {
        createToast({
          title: 'Duplicate code',
          description: `${codeVal} is already associated.`,
          type: ToastTypes.Info,
          duration: 3000,
        });
      }

      return;
    }

    if (patchValue) {
      patchValue({
        referenceCodes: [
          ...(value?.referenceCodes || []),
          {
            codeSet: codeSetVal,
            code: codeVal,
            description: descVal,
          },
        ],
      });
    } else if (
      setVisitForm &&
      typeof complaintIndex === 'number' &&
      typeof conditionIndex === 'number' &&
      !nonConditionReferenceCodes
    ) {
      setVisitForm((prev: any) =>
        updateReferenceCodes(prev, complaintIndex, conditionIndex, {
          codeSet: codeSetVal,
          code: codeVal,
          desc: descVal,
        })
      );
    } else if (setVisitForm && typeof complaintIndex === 'number' && nonConditionReferenceCodes) {
      setVisitForm((prev: any) =>
        updateNonConditionReferenceCodes(prev, complaintIndex, {
          codeSet: codeSetVal,
          code: codeVal,
          desc: descVal,
        })
      );
    }

    setOnDeck({ codeSet: codeSetVal, code: '', desc: '', repeat: false });
    setSearch('');
  };

  const updateReferenceCodesForDeletion = (
    prev: any,
    complaintIdx: number,
    conditionIdx: number,
    items: any[]
  ) => {
    return {
      ...prev,
      soapCustomEditWarning: {
        ...prev.soapCustomEditWarning,
        assessment: false,
      },
      encounterInfo: {
        ...prev.encounterInfo,
        complaints: prev.encounterInfo.complaints.map((complaint: any, i: number) => {
          if (i === complaintIdx) {
            return {
              ...complaint,
              assessment: {
                ...complaint.assessment,
                conditions: complaint.assessment.conditions.map((condition: any, j: number) => {
                  if (j === conditionIdx) {
                    return {
                      ...condition,
                      referenceCodes: [...items],
                    };
                  }

                  return condition;
                }),
              },
            };
          }

          return complaint;
        }),
      },
    };
  };

  const updateNonConditionReferenceCodesForDeletion = (
    prev: any,
    complaintIdx: number,
    items: any[]
  ) => {
    return {
      ...prev,
      soapCustomEditWarning: {
        ...prev.soapCustomEditWarning,
        assessment: false,
      },
      encounterInfo: {
        ...prev.encounterInfo,
        complaints: prev.encounterInfo.complaints.map((complaint: any, i: number) => {
          if (i === complaintIdx) {
            return {
              ...complaint,
              assessment: {
                ...complaint.assessment,
                nonConditions: {
                  ...complaint.assessment.nonConditions,
                  referenceCodes: [...items],
                },
              },
            };
          }

          return complaint;
        }),
      },
    };
  };

  const handleItemDeletion = (
    idx: number,
    formValue: any,
    complaintIdx: number | undefined,
    conditionIdx: number | undefined,
    doPatchValue: ((val: Partial<any>) => void) | null | undefined,
    updateVisitForm: any | null | undefined
  ) => {
    const items = JSON.parse(JSON.stringify(formValue?.referenceCodes || []));
    items.splice(idx, 1);

    if (doPatchValue) {
      doPatchValue({ referenceCodes: [...items] });
    } else if (updateVisitForm && !nonConditionReferenceCodes) {
      if (typeof complaintIdx !== 'number' || typeof conditionIdx !== 'number') {
        // If setVisitForm is being used, complaintIndex and conditionIndex must be numbers
        console.error(
          'Both complaintIndex and conditionIndex must be defined when using setVisitForm'
        );

        return;
      }

      updateVisitForm((prev: any) =>
        updateReferenceCodesForDeletion(prev, complaintIdx, conditionIdx, items)
      );
    } else if (updateVisitForm && nonConditionReferenceCodes) {
      if (typeof complaintIdx !== 'number') {
        // If setVisitForm is being used, complaintIndex must be a number
        console.error('complaintIndex must be defined when using setVisitForm');

        return;
      }

      updateVisitForm((prev: any) =>
        updateNonConditionReferenceCodesForDeletion(prev, complaintIdx, items)
      );
    }
  };

  // Persist a reordered list of codes through the same paths as deletion.
  const handleReorder = (reordered: ReferenceCodeDisplay[]) => {
    if (disabled) return;

    if (patchValue) {
      patchValue({ referenceCodes: [...reordered] });
    } else if (setVisitForm && !nonConditionReferenceCodes) {
      if (typeof complaintIndex !== 'number' || typeof conditionIndex !== 'number') {
        console.error(
          'Both complaintIndex and conditionIndex must be defined when using setVisitForm'
        );

        return;
      }

      setVisitForm((prev: any) =>
        updateReferenceCodesForDeletion(prev, complaintIndex, conditionIndex, reordered)
      );
    } else if (setVisitForm && nonConditionReferenceCodes) {
      if (typeof complaintIndex !== 'number') {
        console.error('complaintIndex must be defined when using setVisitForm');

        return;
      }

      setVisitForm((prev: any) =>
        updateNonConditionReferenceCodesForDeletion(prev, complaintIndex, reordered)
      );
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || !active || active.id === over.id) return;

    const codes: ReferenceCodeDisplay[] = value?.referenceCodes || [];
    const oldIndex = codes.findIndex(c => c.code === active.id);
    const newIndex = codes.findIndex(c => c.code === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    // Re-number so each code's `order` matches its new position; otherwise the
    // stale `order` field would win over array order on save (getBlocks uses
    // `code.order || index + 1`) and the reorder wouldn't persist.
    const reordered = arrayMove(codes, oldIndex, newIndex).map((c, i) => ({
      ...c,
      order: i + 1,
    }));

    handleReorder(reordered);
  };

  const showCodesSection = !!value?.referenceCodes?.length || !hideNoCodesMessage;

  const textSize = useMemo(() => (compact ? 'text-xs' : 'text-sm'), [compact]);

  if (!hasAccess) return null;

  return (
    <>
      {topGutter}
      <div className={klasses.join(' ')}>
        <div
          className={cn(
            textSize,
            'flex text-gray-900 dark:text-dark-gray-100 bg-white dark:bg-dark-gray-800 rounded-t-md p-2 justify-between items-center flex-row',
            showCodesSection ? 'border-b border-gray-300 dark:border-dark-gray-600' : ''
          )}
        >
          <div>
            {label || ''}
            {labelTooltip && (
              <TrivialTooltip
                text={labelTooltip}
                iconClassName="h-3 w-3 text-white inline-block ml-2 align-text-top"
              />
            )}
          </div>
          {rightOfLabel}
        </div>
        {showCodesSection && (
          <div className={cn('flex flex-col gap-2 p-2')}>
            {(!value.referenceCodes || value.referenceCodes.length === 0) &&
              !hideNoCodesMessage && (
                <cite
                  className={cn(
                    textSize,
                    'col-span-10 p-2 ml-2 text-gray-400 dark:text-dark-gray-500'
                  )}
                >
                  {noCodesMessage}
                </cite>
              )}
            {sortable ? (
              <DndContext
                id={dndId}
                sensors={sensors}
                collisionDetection={closestCenter}
                measuring={measuring}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={value.referenceCodes?.map((c: ReferenceCodeDisplay) => c.code) || []}
                  strategy={verticalListSortingStrategy}
                >
                  {value.referenceCodes?.map((o: ReferenceCodeDisplay, idx: number) => (
                    <SortableCodeRow
                      key={o.code}
                      o={o}
                      position={idx + 1}
                      textSize={textSize}
                      disabled={disabled}
                      onDelete={() =>
                        handleItemDeletion(
                          idx,
                          value,
                          complaintIndex,
                          conditionIndex,
                          patchValue,
                          setVisitForm
                        )
                      }
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              value.referenceCodes?.map((o: ReferenceCodeDisplay, idx: number) => {
                return (
                  <div
                    key={`${o.code}`}
                    className={cn('flex flex-row gap-2 items-center', textSize)}
                  >
                    <div className="grow p-2 border-2 rounded-md bg-gray-50 dark:bg-dark-gray-700 border-grey-300 dark:border-dark-gray-600 text-gray-900 dark:text-dark-gray-100">
                      {`${o.code || ''} - ${o.description || ''}`}
                    </div>
                    <div
                      className="hover:text-primary-500 pr-1 active:text-primary-600 flex justify-center shrink pt-2 text-gray-500 dark:text-dark-gray-400 cursor-pointer items-top"
                      style={{ top: 0, right: 0 }}
                      title="Remove this code from the list."
                      onClick={() => {
                        if (disabled) return;
                        handleItemDeletion(
                          idx,
                          value,
                          complaintIndex,
                          conditionIndex,
                          patchValue,
                          setVisitForm
                        );
                      }}
                    >
                      <Icon icon={Icons.trash} className="w-5 h-5" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
        <div className="flex flex-row gap-2 p-2 items-center border-t border-gray-300 dark:border-dark-gray-600">
          <CodeAutocomplete
            endpoint={endpoint}
            search={search}
            setSearch={setSearch}
            value={onDeck.code as string}
            weighted={true}
            className={`grow max-w-96`}
            noneText={noneText}
            onChange={(val: string) => {
              const existingCodes = (value?.referenceCodes || []).reduce((acc: any, curr: any) => {
                  return { ...acc, [curr.code]: curr };
                }, {}),
                repeat = !!existingCodes[val];
              setOnDeck(p => ({ ...p, code: val, repeat }));
            }}
            codeSet={codeSet}
            onChangeDescription={(val: any) => {
              const desc = val?.data?.description || '';
              const codeSet2 = val?.data?.codeSet || '';
              const codeFromOption = val?.value || onDeck.code || '';
              const next = { ...onDeck, desc, codeSet: codeSet2, code: codeFromOption };
              setOnDeck(next);
              const existingCodes = (value?.referenceCodes || []).reduce(
                (acc: any, curr: any) => ({ ...acc, [curr.code]: curr }),
                {}
              );
              const repeat = !!existingCodes[codeFromOption];

              if (codeFromOption && !repeat) {
                addCodeFromValues(next.codeSet, codeFromOption, next.desc);
              } else if (codeFromOption && repeat) {
                if (createToast) {
                  createToast({
                    title: 'Duplicate code',
                    description: `${codeFromOption} is already associated.`,
                    type: ToastTypes.Info,
                    duration: 3000,
                  });
                }

                // Clear the current selection and input when duplicate is selected
                setOnDeck({ codeSet: next.codeSet, code: '', desc: '', repeat: false });
                setSearch('');
              }
            }}
            descriptionAsString={false}
            clinicId={clinicId}
            local={local}
          />
        </div>
        {!!associatedCodes?.length && !!filteredCodes?.length ? (
          <div className={cn('flex flex-col p-2 gap-1', textSize)}>
            <p className="text-sm">{associatedCodesLabel}</p>
            <div className={cn('flex flex-col gap-1')}>
              {filteredCodes?.map((code, _i) => {
                return (
                  <div key={code.code} className="flex flex-row gap-1">
                    <div
                      className={cn(
                        textSize,
                        'flex grow text-ellipsis bg-gray-50 dark:bg-dark-gray-700 border text-gray-600 dark:text-dark-gray-100 text-sm px-2 py-2 rounded-md border-gray-300 dark:border-dark-gray-600 cursor-pointer'
                      )}
                      onClick={() => addCodeFromValues(code.codeSet, code.code, code.description)}
                    >{`${code.code}: ${code.description}`}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};
